import { useEffect, useState } from "react";
import { ShieldAlert, ExternalLink, Disc3, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAllSmartlinks } from "@/lib/afmApi";
import { fmtNum } from "@/lib/format";
import { Loading, SectionTitle } from "@/components/common";

const clicksTotal = (c) => {
  if (typeof c === "number") return c;
  if (c && typeof c === "object") return Object.values(c).reduce((a, b) => a + (Number(b) || 0), 0);
  return 0;
};

export default function AdminSmartlinks() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    getAllSmartlinks(user.id)
      .then((d) => { setItems(d?.smart_links || []); setStatus("ok"); })
      .catch((e) => { setMsg(e?.response?.data?.detail || "Acceso restringido"); setStatus("denied"); });
  }, [user.id]);

  if (status === "loading") return <Loading />;
  if (status === "denied") return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="admin-denied">
      <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
      <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-500">{msg}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-smartlinks-page">
      <SectionTitle>Smart Links</SectionTitle>
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Lanzamiento</th>
                <th className="px-5 py-3 font-semibold">Artista</th>
                <th className="px-5 py-3 font-semibold">Slug</th>
                <th className="px-5 py-3 text-right font-semibold">Clicks</th>
                <th className="px-5 py-3 text-center font-semibold">Link</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`smartlink-row-${s.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                        {s.cover_url ? <img src={s.cover_url} alt="" className="h-full w-full object-cover" /> : <Disc3 className="h-4 w-4 text-slate-300" />}
                      </div>
                      <span className="font-medium text-black">{s.title}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{s.artist_name}</td>
                  <td className="px-5 py-3"><span className="inline-flex items-center gap-1 font-mono text-xs text-slate-500"><Link2 className="h-3 w-3" />/l/{s.slug}</span></td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-black">{fmtNum(clicksTotal(s.clicks))}</td>
                  <td className="px-5 py-3 text-center"><a href={`/l/${s.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex text-slate-400 hover:text-black"><ExternalLink className="h-4 w-4" /></a></td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-400">No hay smart links.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
